import { HttpClient } from "@angular/common/http";
import {
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { NbDialogService } from "@nebular/theme";
import { Store } from "@ngrx/store";
import { TableColumn } from "@swimlane/ngx-datatable";
import * as flat from "flat";
import { combineLatest, timer } from "rxjs";
import { debounce, filter, switchMap, withLatestFrom } from "rxjs/operators";
import {
  AppState,
  ExtDuration,
  HistoryItem,
  StatisticItem,
} from "../app.models";
import { selectCustomerApiEndpoint, selectRouteParams } from "../app.selector";
import { AppColFromIdsPipe } from "../components/pipes/AppColPipe";
import { DateTimePipe } from "../components/pipes/DateTimePipe";
import {
  StatisticsPipe,
  StatisticsPipeExtDuration,
} from "../components/pipes/StatisticsPipe";
import { TagColFromIdsPipe } from "../components/pipes/TagColPipe";

@Component({
  selector: "app-box-archive",
  templateUrl: "./box-archive.component.html",
  styleUrls: ["./box-archive.component.scss"],
})
export class BoxArchiveComponent implements OnInit {
  $context: any;
  clientId: any;
  endpoint: any;
  $history: any;
  columns: TableColumn[];
  history: HistoryItem[];
  historyLimit = 5;
  hasExternalDuration: boolean;
  @Input() type: "apps" | "tags";
  @Input() id: number;
  @ViewChild("nameHdrTpl") nameHdrTpl: TemplateRef<any>;
  @ViewChild("detailsTemplate") detailsTemplate: TemplateRef<any>;
  @ViewChild("payloadDialog") payloadDialog: TemplateRef<any>;

  constructor(
    private http: HttpClient,
    private store: Store,
    private statisticsPipe: StatisticsPipe,
    private statsExtDuration: StatisticsPipeExtDuration,
    private dialogService: NbDialogService
  ) {}

  ngOnInit(): void {
    //queue updates after testpush
    const queue$ = this.store
      .select((state: AppState) => state.queue)
      .pipe(debounce(() => timer(1000)));
    // context (routing + endpoints)
    const context$ = this.store.select(selectCustomerApiEndpoint).pipe(
      filter((c) => !!c),
      withLatestFrom(this.store.select(selectRouteParams))
    );

    //set context to component
    this.$context = context$.subscribe(([endpoint, params]) => {
      this.clientId = params.clientId;
      this.endpoint = endpoint;
    });

    // fetch history
    this.$history = combineLatest([context$, queue$])
      .pipe(
        // this.$history = context$.pipe(
        switchMap(() =>
          this.http.get<HistoryItem[]>(
            `${this.endpoint}/history/${this.type}/${this.id}`
          )
        )
      )
      .subscribe((history) => {
        this.columns = [
          {
            name: "Details",
            prop: "details",
            headerTemplate: this.nameHdrTpl,
            cellTemplate: this.detailsTemplate,
            maxWidth: 100,
            minWidth: 80,
          },
          {
            name: "Time",
            prop: "time",
            headerTemplate: this.nameHdrTpl,
            pipe: new DateTimePipe(),
            minWidth: 140,
          },
          {
            name: "Tags",
            prop: "tagIds",
            headerTemplate: this.nameHdrTpl,
            pipe: new TagColFromIdsPipe(this.store),
          },
          {
            name: "Apps",
            prop: "appIds",
            headerTemplate: this.nameHdrTpl,
            pipe: new AppColFromIdsPipe(this.store),
          },
          {
            name: "Devices",
            prop: "stats",
            headerTemplate: this.nameHdrTpl,
            pipe: this.sumStats("totalCount"),
            maxWidth: 100,
          },
        ];
        this.history = history;
        this.setPage({ offset: 0, limit: this.historyLimit });
      });
  }

  sumStats(key) {
    return { transform: (value) => this.statisticsPipe.transform(value, key) };
  }

  flattenPayload(payload: string) {
    return flat(JSON.parse(payload));
  }

  parsePayload(payload: string) {
    return JSON.parse(payload);
  }

  handleClickJSON(context) {
    console.log(context.stats);
    this.dialogService.open(this.payloadDialog, { context });
  }

  setPage(pageInfo) {
    const { limit, offset } = pageInfo;
    const start = offset * limit;
    const end = start + limit;
    for (let index = start; index < end; index++) {
      if (!this.history[index]) {
        return;
      }
      this.http
        .get<StatisticItem[]>(
          `${this.endpoint}/stats/jobs/${this.history[index]["id"]}`
        )
        .subscribe((stats) => {
          this.history[index]["stats"] = stats;
          this.hasExternalDuration = this.findHasExternalDuration(stats);
          this.history = [...this.history];
        });
    }
  }

  findHasExternalDuration(stats: StatisticItem[]) {
    if (!stats) {
      return false;
    }
    if (this.statsExtDuration.transform(stats) === "0") {
      return false;
    } else {
      const extDurationArr = this.statsExtDuration.transform(
        stats
      ) as ExtDuration[];
      if (
        extDurationArr &&
        extDurationArr.filter((app) => app && app.extDuration).length > 0
      ) {
        return true;
      }
      return false;
    }
  }

  ngOnDestroy() {
    this.$history.unsubscribe();
    this.$context.unsubscribe();
  }
}
